/* ============================================================================
   occasion-dated-multi-add-fix.js  —  v2026-08-15-r8 (change-event capture + live price)
   ----------------------------------------------------------------------------
   Three responsibilities on the custom Mother's Day product page:

   (A) Multi-category Giftship upsell add-to-cart fix.
   (B) Live ATC price update — recomputes the price shown in `.mdp-atc__price`
       as the customer ticks/unticks Giftship upsells and changes basket
       variants. Prices come from `data-child-option-price` on each upsell's
       `<li>` ancestor (Giftship-rendered, raw decimal) and from the
       `[data-mdp-variants]` JSON for the main basket variant (cents).
   (C) Required-field pre-flight — read-only guard that stops (A) from
       bypassing Giftship's own validation. Added 2026-07-25, see the
       "Required-field pre-flight" block below for the bug it closes.
       EXTENDED 2026-08-14 to cover required fields that are HIDDEN because
       their upsell's category was switched away from, while the upsell itself
       is still in the captured set and therefore still being added. Same
       block below.
       EXTENDED AGAIN 2026-08-15 because that 2026-08-14 pass searched only
       INSIDE the captured upsell's own option block, and the blanket name
       field is a SIBLING block, so the hidden case still sailed through on
       both themes. Pass 2b below sweeps the whole container instead, using
       `disabled` as the block-is-inactive signal.

   ---------------------------------------------------------------------------
   Bug: On the custom Mother's Day product page, ticking upsells from multiple
   Giftship Image Gallery categories then clicking ATC only added the last
   category's selection — earlier ones were silently dropped.

   Earlier diagnosis showed the form's FormData has both id[] entries DURING
   user interaction, but by the time ATC click handlers fire, only the
   currently-active category's id[] survive checked. Giftship's React tree
   preserves state across category switches but synchronizes back to the form
   only for the visible category at submit time.

   Fix: drive an accumulated "captured" set off `change` events on the id[]
   checkboxes themselves.
     - User ticks a checkbox → change event → add to captured
     - User unticks a checkbox → change event → remove from captured
     - Giftship programmatically unchecks during category switch → does NOT
       fire `change` (programmatic .checked = false is silent) → captured
       retains the value

   At ATC, intercept the click, read `captured`, do main + bulk POST.

   Untick edge case: handled correctly because user-driven untick DOES fire
   the change event, so captured releases it.

   Discovered: 2026-05-01.
   ============================================================================ */
window.__mdayMultiAddLoaded = Date.now();
(function () {
  'use strict';
  var TAG = '[mday2026-multi-add]';

  // captured[variantIdString] = { price: <decimal-dollars> }
  // Driven by user `change` events on id[] checkboxes; survives Giftship's
  // category-switch programmatic unchecks (those don't fire change). Price is
  // captured at tick-time so the live ATC display can sum it even when
  // Giftship has hidden the source checkbox after a category switch.
  var captured = Object.create(null);

  function init() {
    document.addEventListener('change', handleChange, true);
    document.addEventListener('click', handleAnyClick, true);
    // Pick up any pre-checked id[] (e.g., remembered from a page-state restore).
    setTimeout(seedFromCurrentDom, 1000);
    setTimeout(seedFromCurrentDom, 3000);
    // Price display can also be wrong on initial paint if Giftship populates
    // late or a variant is pre-selected. Recompute a few times.
    setTimeout(updatePriceDisplay, 800);
    setTimeout(updatePriceDisplay, 2500);
  }

  function handleChange(e) {
    var t = e.target;
    if (!t || t.tagName !== 'INPUT') return;

    // (A) Track id[] checkbox state for multi-add.
    if (t.name === 'id[]') {
      if (t.checked) {
        recordCapture(t);
      } else if (t.value) {
        delete captured[t.value];
      }
      // (B) Live price update on every upsell tick/untick.
      updatePriceDisplay();
      return;
    }

    // (B) Live price update on basket variant change. The existing
    // occasion-dated-product-page.js updates [data-mdp-variant-id] when the user
    // changes a variant select; we wait one tick for that to land then re-render.
    if (t.classList && t.classList.contains('mdp-variant-select')) {
      setTimeout(updatePriceDisplay, 50);
      return;
    }

    // (C) Auto-scroll into view when the customer picks a Giftship upsell
    // category. Without this, the expanded option list opens BELOW the
    // category gallery and the customer doesn't realise their click did
    // anything — they have to scroll to find the new options.
    if (t.name === 'properties[add-mothersday-options]' && e.isTrusted) {
      // Wait for Giftship's React to render the expanded options.
      setTimeout(scrollToActiveOptions, 250);
    }
  }

  function scrollToActiveOptions() {
    // Find the first non-disabled upsell checkbox — it's in the active
    // (just-revealed) category. Walk up to a parent we can scroll to.
    var firstCb = document.querySelector('#gsAppContainer input[name="id[]"]:not(:disabled)');
    if (!firstCb) return;
    var listEl = firstCb.closest('ul, ol, [data-child-name], .gs__product-option') || firstCb.parentElement;
    if (!listEl) return;
    var rect = listEl.getBoundingClientRect();
    // Scroll so the option list's top sits below the viewport top by an
    // offset that leaves the category gallery visible above for context.
    var headerOffset = 180;
    var targetScroll = window.scrollY + rect.top - headerOffset;
    // Only scroll DOWNWARD to reveal — never bounce the customer back up
    // (which would feel jarring if they were already past the category area).
    if (targetScroll > window.scrollY + 20) {
      window.scrollTo({ top: targetScroll, behavior: 'smooth' });
    }
  }

  function seedFromCurrentDom() {
    var checkboxes = document.querySelectorAll('input[name="id[]"]:checked');
    checkboxes.forEach(recordCapture);
  }

  function recordCapture(cb) {
    if (!cb || !cb.value || !/^\d{6,}$/.test(cb.value)) return;
    var li = cb.closest && cb.closest('[data-child-option-price]');
    var price = 0;
    if (li) {
      var p = parseFloat(li.getAttribute('data-child-option-price'));
      if (!isNaN(p) && p > 0) price = p;
    }
    captured[cb.value] = { price: price };
  }

  function handleAnyClick(e) {
    var atc = e.target && e.target.closest && e.target.closest('[data-mdp-atc]');
    if (!atc) return;
    var form = atc.closest('form[id^="mdp-product-form-"]');
    if (!form) return;
    handleAtcClick(e, atc, form);
  }

  function handleAtcClick(e, atcBtn, form) {
    var upsellIds = Object.keys(captured);
    var mainId = readMainVariantId(form);

    if (!mainId) {
      console.warn(TAG, 'no main variant — letting native flow run');
      return;
    }

    if (upsellIds.length === 0) {
      // No upsells — let the form submit natively (single-product purchase).
      return;
    }

    // This handler is about to bypass the native submit, and with it Giftship's
    // own validation. Block the bypass while a required field is still empty.
    var invalid = firstInvalidRequired(form);
    if (invalid) {
      e.preventDefault();
      e.stopImmediatePropagation();
      e.stopPropagation();
      // REVEAL RATHER THAN INSTRUCT, added 2026-08-15. Tests 1 to 3 of the
      // 2026-08-15 pass all blocked correctly, but the blocked shopper was left
      // staring at a collapsed block and a message telling them to reopen an
      // option they could not see. So before deciding what to say, try to SHOW
      // the field.
      //
      // `data-gs-addon-view` is a THEME-OWNED attribute on <body>, written by
      // giftship-occasion-bridge.js, and the tidy-view rules in
      // occasion-dated-product-page.css collapse the blanket blocks only while
      // it reads "other". Setting it to "blanket" lifts that collapse, and the
      // fields come back filled in and still ACTIVE in Giftship's rule engine,
      // because the _blanket_on flag kept them so. This writes to <body> and
      // nothing else, so the Gist constraint holds: no writes inside
      // #gsAppContainer, no focus(), no reportValidity().
      //
      // KNOWN LIMIT, deliberate: the bridge's setView recomputes this attribute
      // from the gallery's checked value on the next gallery click, so the
      // reveal lasts until the customer touches the gallery again. That is
      // exactly long enough to type the name, which is all this needs to do.
      var stillHidden = invalid.hidden;
      if (invalid.hidden) {
        document.body.setAttribute('data-gs-addon-view', 'blanket');
        // Re-read after the reveal. Reading offsetParent forces layout, so the
        // CSS change above has already applied by the time this is evaluated.
        // If the field is showing now, this is an ordinary visible block from
        // here on: red styling, scroll to the field, the normal message.
        stillHidden = !invalid.el || invalid.el.offsetParent === null;
      }
      // A STILL-HIDDEN blocking field cannot show the red styling and is not
      // worth scrolling to, so aim at the Giftship container instead and let
      // the message say which option to reopen. That is also the
      // captured-but-missing case, where there is no element at all. Scrolling
      // reads a box and moves the window; it alters nothing inside
      // #gsAppContainer. The visible case is unchanged: same target, same
      // message, same behaviour.
      var scrollTarget = stillHidden
        ? document.getElementById('gsAppContainer')
        : invalid.el;
      if (scrollTarget) {
        try {
          scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } catch (err) {
          console.warn(TAG, 'scrollIntoView failed:', err);
        }
      }
      showRequiredMessage(atcBtn, stillHidden ? MSG_TEXT_HIDDEN : MSG_TEXT);
      document.body.classList.add('mdp-required-error');
      // ATC stays ENABLED on purpose: the customer fixes the field and clicks
      // again. No fetch, no disable, no state left behind.
      return;
    }

    clearRequiredMessage();

    e.preventDefault();
    e.stopImmediatePropagation();
    e.stopPropagation();

    var atcLabel = atcBtn.querySelector('.mdp-atc__label');
    atcBtn.disabled = true;
    if (atcLabel) {
      atcLabel.dataset.originalText = atcLabel.textContent;
      atcLabel.textContent = 'Adding…';
    }

    var properties = readProperties(form);
    var quantity = parseInt(new FormData(form).get('quantity'), 10) || 1;

    addMainThenBulk(mainId, quantity, properties, upsellIds)
      .then(function () {
        window.location.href = '/cart';
      })
      .catch(function (err) {
        console.warn(TAG, 'add failed:', err);
        atcBtn.disabled = false;
        if (atcLabel && atcLabel.dataset.originalText) atcLabel.textContent = atcLabel.dataset.originalText;
        alert('We couldn\'t add all the items to your cart. Please try again, or contact us if it keeps happening.');
      });
  }

  // ---------------------------------------------------------------------------
  // Required-field pre-flight (responsibility C)
  // ---------------------------------------------------------------------------
  // Giftship validates on the NATIVE submit path. The moment this file
  // intercepts the ATC click and POSTs straight to /cart/add.js, that validation
  // never runs, so required Giftship fields submit empty. Reproduced on live
  // 2026-07-25: tick the $10 blanket upsell, type no name, add to cart succeeds
  // and the basket line carries _personalize:"".
  //
  // This check is STRICTLY READ-ONLY. Per Gist support guidance, theme JS must
  // not alter Giftship-managed inputs or anything inside #gsAppContainer: no
  // focus(), no reportValidity(), no property writes, no injected elements in
  // there. We read attributes and values only, and render our own message into
  // theme-owned DOM outside the container.
  var REQUIRED_SEL = 'input[required], textarea[required], select[required]';
  var MSG_ID = 'mdp-atc-required-msg';
  // REWORDED 2026-08-15, after all five tests passed, to name the thing the
  // shopper actually has to do rather than describe the form's state.
  // KNOWN NARROWNESS, the same carry-forward already recorded on
  // MSG_TEXT_HIDDEN below: this copy names the baby's name specifically, which
  // is correct only while the blanket is the ONLY occasion with a required
  // Giftship field. The moment another occasion gains one, BOTH strings need
  // generalising, or a shopper buying something with no blanket in it is told
  // we need a baby's name. The guard's logic is already occasion-agnostic;
  // only these two strings are not.
  var MSG_TEXT = 'Please add the baby\'s name for the blanket, highlighted in red below, then add to cart.';
  // Used when the blocking field is HIDDEN, which is what happens after a
  // Giftship category switch. The red field styling cannot be seen in that
  // state and there is no useful field to scroll to, so the copy has to say
  // which option to reopen. Added 2026-08-14.
  // REWORDED 2026-08-15, and DEMOTED TO A FALLBACK. It said "the blanket option
  // above", which was wrong on the page: the message renders under the ATC
  // button and the blanket option sits BELOW it. The copy is now
  // direction-neutral, because after the reveal added below this is only
  // reached when the field could not be shown at all, and there is then no
  // reliable direction to point in.
  var MSG_TEXT_HIDDEN = 'You added blanket personalization and we still need the baby\'s name. Please reselect the blanket option and type the name, then add to cart.';

  function skipForValidation(el) {
    if (el.disabled) return true;
    if (el.offsetParent === null) return true;
    if (el.closest && el.closest('[hidden]')) return true;
    return false;
  }

  // Which Giftship option block owns a captured upsell id. Giftship hides a
  // deselected category IN PLACE rather than unmounting it, so the checkbox for
  // a captured upsell is still in the DOM after a category switch even though
  // it is not visible. Strictly read-only: this queries and walks, and writes
  // nothing inside #gsAppContainer.
  function findCapturedBlock(id) {
    var gsRoot = document.getElementById('gsAppContainer');
    if (!gsRoot) return { found: false, block: null };
    var cb = gsRoot.querySelector('input[name="id[]"][value="' + id + '"]');
    if (!cb) return { found: false, block: null };
    var block = null;
    if (cb.closest) {
      block = cb.closest('[data-child-name]') ||
              cb.closest('[data-child-option-price]') ||
              cb.closest('li') ||
              cb.parentElement;
    }
    return { found: true, block: block || null };
  }

  // Pass 2 of the pre-flight, added 2026-08-14. The second post-fix re-count
  // found 6 of 27 orders still missing the name, all babygirl, all with SKU
  // 5001 charged and no _personalize key on the main basket line. Live repro:
  // tick the blanket, leave the name blank, switch to another Giftship category,
  // add to cart. `captured` retains the upsell across that switch BY DESIGN,
  // which is the whole point of responsibility (A), but pass 1 skips the
  // now-hidden name field on skipForValidation's offsetParent test, so nothing
  // blocked the sale and the line was charged with no name.
  //
  // So: for every id still in `captured`, the required fields inside ITS option
  // block must be filled, hidden or not. Required fields belonging to upsells
  // that are NOT captured stay skipped when hidden, exactly as before, because
  // those upsells are not being added to the cart.
  //
  // `disabled` is deliberately NOT skipped in this pass. A disabled empty field
  // submits nothing, yet the captured upsell is POSTed regardless, which is the
  // same silent drop this guard exists to stop.
  function firstInvalidCapturedHidden() {
    var ids = Object.keys(captured);
    for (var i = 0; i < ids.length; i++) {
      var id = ids[i];
      // recordCapture only stores digit ids, so this also keeps the attribute
      // selector below well formed.
      if (!/^\d{6,}$/.test(id)) continue;
      var hit = findCapturedBlock(id);
      if (!hit.found) {
        // Captured, but its checkbox is not in the DOM at all, so its required
        // fields cannot be read. Block instead of guessing: requiring the name
        // always wins over completing the sale. Ruled by Tommy 2026-08-14.
        console.warn(TAG, 'captured upsell not found in DOM, blocking:', id);
        return { el: null, hidden: true, id: id };
      }
      if (!hit.block) continue;
      var fields = hit.block.querySelectorAll(REQUIRED_SEL);
      for (var j = 0; j < fields.length; j++) {
        var f = fields[j];
        var fVal = f.value == null ? '' : String(f.value);
        if (fVal.trim() === '') {
          return { el: f, hidden: f.offsetParent === null, id: id };
        }
      }
    }

    // Pass 2b, added 2026-08-15: a container-wide sweep of every ENABLED
    // required field, run after the per-captured-block loop above finds
    // nothing.
    //
    // THE GAP IT CLOSES. The 2026-08-14 pass looks only INSIDE the captured
    // upsell's own option block, but the blanket name field (_personalize) is a
    // SIBLING block of the upsell checkbox's block, not a descendant of it, so
    // that loop never saw it. Tick the blanket, leave the name blank, switch
    // Giftship category, add to cart, and the sale went through with an empty
    // name on BOTH themes. Proven 2026-08-15 by browser tests on live and on
    // staging plus DOM captures.
    //
    // WHY `disabled` IS THE ACTIVITY SIGNAL HERE. Giftship disables the fields
    // of INACTIVE blocks. The 2026-08-15 captures read disabled:true
    // required:false on an inactive block, and disabled:false required:true on
    // a block that is ACTIVE but merely hidden by the category switch. So
    // skipping disabled fields sweeps exactly the blocks the rule engine still
    // holds active, hidden or not, and never blocks on one it has switched off.
    // This deliberately INVERTS pass 2's "disabled is not skipped" rule, and
    // the difference is scope: pass 2 is confined to one captured upsell's own
    // block, this sweep is not confined at all, so without the disabled test it
    // would block on every dormant required field in the container.
    //
    // WHEN IT RUNS. Only ever with a non-empty `captured` set:
    // firstInvalidCapturedHidden is reached only from firstInvalidRequired,
    // which handleAtcClick consults only when upsellIds.length > 0. Someone
    // buying the basket on its own never reaches this code.
    //
    // Still strictly read-only, per the Gist constraint: this queries and reads
    // attributes and values, and writes nothing inside #gsAppContainer.
    var gsRoot = document.getElementById('gsAppContainer');
    if (!gsRoot) return null;
    var allFields = gsRoot.querySelectorAll(REQUIRED_SEL);
    for (var k = 0; k < allFields.length; k++) {
      var af = allFields[k];
      if (af.disabled) continue;
      var afVal = af.value == null ? '' : String(af.value);
      if (afVal.trim() === '') {
        return { el: af, hidden: af.offsetParent === null };
      }
    }
    return null;
  }

  // Returns null when nothing blocks, otherwise a descriptor:
  //   { el: <element|null>, hidden: <bool>, id: <captured id, pass 2 only> }
  // `el` is null only in the captured-but-missing case, where there is no field
  // to point at. Callers MUST branch on `hidden`, because a hidden field cannot
  // show the red styling and is not worth scrolling to.
  function firstInvalidRequired(form) {
    var roots = [];
    var gsRoot = document.getElementById('gsAppContainer');
    if (gsRoot) roots.push(gsRoot);
    if (form) roots.push(form);

    // The form usually CONTAINS #gsAppContainer, so the two roots overlap.
    // Walk gsAppContainer first (that is where the reported bug lives) and skip
    // anything already seen.
    var seen = [];
    for (var i = 0; i < roots.length; i++) {
      var found = roots[i].querySelectorAll(REQUIRED_SEL);
      for (var j = 0; j < found.length; j++) {
        var el = found[j];
        if (seen.indexOf(el) !== -1) continue;
        seen.push(el);
        if (skipForValidation(el)) continue;
        var val = el.value == null ? '' : String(el.value);
        if (val.trim() === '') return { el: el, hidden: false };
      }
    }

    // Nothing VISIBLE is empty. Only now look at the hidden fields of upsells
    // the customer has already captured. Running pass 2 second is what keeps
    // pass 1's behaviour and its message exactly what they were, so this change
    // can only add a block where the old code let one through.
    return firstInvalidCapturedHidden();
  }

  function showRequiredMessage(atcBtn, text) {
    // Defensive: never inject anything inside Giftship's container. The ATC
    // button is theme markup and sits outside it, but if that ever changes we
    // warn instead of violating the rule.
    if (atcBtn.closest && atcBtn.closest('#gsAppContainer')) {
      console.warn(TAG, 'ATC button is inside #gsAppContainer — message not injected');
      return;
    }
    var msg = document.getElementById(MSG_ID);
    if (!msg) {
      msg = document.createElement('div');
      msg.id = MSG_ID;
      msg.setAttribute('role', 'alert');
      atcBtn.insertAdjacentElement('afterend', msg);
    }
    msg.style.marginTop = '10px';
    msg.style.fontSize = '16px';
    msg.style.fontWeight = '700';
    msg.style.lineHeight = '1.4';
    msg.style.padding = '12px 14px';
    msg.style.borderRadius = '4px';
    msg.style.background = '#a10c1f';
    msg.style.border = 'none';
    msg.style.color = '#ffffff';
    msg.style.display = 'block';
    // Falls back to the original text if a caller omits it, so the 2026-07-25
    // behaviour survives any future call site that does not pass one.
    msg.textContent = text || MSG_TEXT;
  }

  function clearRequiredMessage() {
    document.body.classList.remove('mdp-required-error');
    var msg = document.getElementById(MSG_ID);
    if (!msg) return;
    msg.textContent = '';
    msg.style.display = 'none';
  }

  function readMainVariantId(form) {
    var attrInput = form.querySelector('[data-mdp-variant-id]');
    if (attrInput && attrInput.value && /^\d+$/.test(attrInput.value)) return attrInput.value;
    var fd = new FormData(form);
    var single = fd.get('id');
    if (single && /^\d+$/.test(single)) return single;
    return null;
  }

  function readProperties(form) {
    var properties = {};
    new FormData(form).forEach(function (value, key) {
      var m = key.match(/^properties\[(.+)\]$/);
      if (m) properties[m[1]] = value;
    });
    return properties;
  }

  // ---------------------------------------------------------------------------
  // Live ATC price display (responsibility B)
  // ---------------------------------------------------------------------------
  var variantsCache = null;

  function getVariantsMap() {
    if (variantsCache) return variantsCache;
    var script = document.querySelector('[data-mdp-variants]');
    if (!script || !script.textContent) return null;
    try {
      var arr = JSON.parse(script.textContent);
      if (!Array.isArray(arr)) return null;
      variantsCache = Object.create(null);
      arr.forEach(function (v) {
        if (v && v.id != null && v.price != null) {
          variantsCache[String(v.id)] = v.price; // price is in cents
        }
      });
      return variantsCache;
    } catch (e) {
      return null;
    }
  }

  function readMainPriceCents(form) {
    var mainId = readMainVariantId(form);
    if (!mainId) return null;
    var map = getVariantsMap();
    if (!map) return null;
    return map[String(mainId)] != null ? map[String(mainId)] : null;
  }

  function sumUpsellCents() {
    // Sum from the captured set (source of truth), NOT the DOM. After a
    // category switch, prior categories' checkboxes are programmatically
    // unchecked by Giftship — so :checked won't include them — but they're
    // still in `captured` (with their price) until the user explicitly unticks.
    var total = 0;
    Object.keys(captured).forEach(function (id) {
      var p = captured[id] && captured[id].price;
      if (typeof p === 'number' && p > 0) {
        total += Math.round(p * 100);
      }
    });
    return total;
  }

  function formatMoney(cents) {
    var dollars = (cents / 100).toFixed(2);
    var parts = dollars.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return '$' + parts.join('.');
  }

  function updatePriceDisplay() {
    try {
      var form = document.querySelector('form[id^="mdp-product-form-"]');
      if (!form) return;
      var priceEl = form.querySelector('.mdp-atc__price');
      if (!priceEl) return;
      var mainCents = readMainPriceCents(form);
      if (mainCents == null) return; // bail silently — leave Liquid render alone
      var upsellCents = sumUpsellCents();
      var totalFormatted = formatMoney(mainCents + upsellCents);

      // Main ATC button — single total.
      priceEl.textContent = totalFormatted;

      // Bottom sticky bar — total + breakdown. The breakdown row only shows
      // when there's something added on; otherwise customers don't need to
      // see "Basket $X + Add-ons $0".
      var stickyPriceEl = document.querySelector('.mdp-sticky-atc__price-current');
      if (stickyPriceEl) {
        stickyPriceEl.textContent = totalFormatted;
      }
      var breakdownEl = document.querySelector('[data-mdp-sticky-breakdown]');
      if (breakdownEl) {
        if (upsellCents > 0) {
          var baseEl = breakdownEl.querySelector('.mdp-sticky-atc__breakdown-base');
          var extrasEl = breakdownEl.querySelector('.mdp-sticky-atc__breakdown-extras');
          if (baseEl) baseEl.textContent = formatMoney(mainCents);
          if (extrasEl) extrasEl.textContent = formatMoney(upsellCents);
          breakdownEl.removeAttribute('hidden');
        } else {
          breakdownEl.setAttribute('hidden', '');
        }
      }
    } catch (err) {
      console.warn(TAG, 'price update failed:', err);
    }
  }

  function addMainThenBulk(mainId, quantity, properties, upsellIds) {
    var mainBody = { id: mainId, quantity: quantity };
    if (Object.keys(properties).length > 0) mainBody.properties = properties;

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(mainBody)
    }).then(function (res) {
      if (!res.ok) throw new Error('Main add failed (HTTP ' + res.status + ')');
      return res.json();
    }).then(function () {
      var clean = upsellIds.filter(function (id) { return id !== String(mainId); });
      if (clean.length === 0) return;
      var items = clean.map(function (id) { return { id: parseInt(id, 10), quantity: 1 }; });
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: items })
      }).then(function (res) {
        if (!res.ok) throw new Error('Bulk upsell add failed (HTTP ' + res.status + ')');
        return res.json();
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
